import { useCallback, useEffect, useRef, useState } from "react";
import {
    Award, Check, Clock, Crown, Flame, Globe, Lock, Medal, Trophy, X, Zap,
} from "lucide-react";
import { useTranslation } from "../../../i18n";
import { playMilestoneFanfare } from "../../helpers/audioPlayer";
import { celebrationSoundEnabled, trophyGateConfig } from "../pointsSettings";
import {
    DAY_LADDER,
    GOLDEN_HOUR_LADDER,
    LIFETIME_LADDER,
    FLOW_SHORT_KEY,
    RECORD_WINDOWS_MIN,
    STREAK_LADDER,
    TROPHY_CELEBRATION_EVENT,
    TROPHY_DEPARTMENTS,
    TROPHY_OPEN_EVENT,
    daysSince,
    fetchTrophies,
    formatPoints,
    markTrophiesSeen,
    nextThreeRule,
    noteTrophyOpen,
    type TrophyCelebration,
    type TrophyFlow,
    type TrophySnapshot,
} from "../trophies";
import "../../../styles/trophies.css";

/**
 * The full-screen My Trophies modal.
 *
 * Mounted once, app-wide (like MilestoneCelebration): the score pill opens it
 * via TROPHY_OPEN_EVENT, and a fresh record or milestone opens it ITSELF via
 * TROPHY_CELEBRATION_EVENT — fireworks twinkle behind the page, a plaque
 * announces what just happened, and the list below shows the new number in
 * place. Closing acknowledges: quiet milestone grants and dethronement
 * notices stop being "new".
 *
 * Scanning keeps working underneath — scan input is a global keydown listener
 * and nothing here captures the keyboard.
 */

// A scan arriving while the modal is open dismisses it — but not within this
// window of opening, or the trailing keys of the scan that earned the
// celebration would close it unseen.
export const SCAN_DISMISS_GRACE_MS = 400;

// Static firework positions (left%, top%, size px, color, delay s).
const SPARKS: [number, number, number, string, number][] = [
    [8, 12, 8, "#FF7315", 0], [22, 7, 5, "#f59e0b", 0.3], [88, 10, 7, "#ec4899", 0.2],
    [78, 16, 5, "#3b82f6", 0.8], [50, 6, 6, "#eab308", 0.5], [33, 15, 4, "#22c55e", 0.9],
    [68, 12, 5, "#a855f7", 0.6], [12, 38, 6, "#ec4899", 0.4], [90, 36, 6, "#22c55e", 1.0],
    [6, 68, 5, "#3b82f6", 0.7], [92, 74, 6, "#FF7315", 0.1], [48, 90, 5, "#f59e0b", 1.1],
];

interface StoredUser { uid?: number; name?: string; warehouse?: string }

const storedUser = (): StoredUser => {
    try {
        const raw = window.localStorage.getItem("user");
        return raw ? (JSON.parse(raw) as StoredUser) : {};
    } catch {
        return {};
    }
};

const TrophyModal = () => {
    const { t, language } = useTranslation();
    const [open, setOpen] = useState(false);
    const [tab, setTab] = useState<"milestones" | "records">("milestones");
    const [flow, setFlow] = useState<TrophyFlow>("sorter_1");
    const [snap, setSnap] = useState<TrophySnapshot | null>(null);
    const [celebration, setCelebration] = useState<TrophyCelebration | null>(null);
    // The self-close countdown: viewing time is rationed (a warehouse
    // setting, default 20s). Deadline-based so a re-render can't stretch it.
    const [expiresAt, setExpiresAt] = useState<number | null>(null);
    const [secondsLeft, setSecondsLeft] = useState<number>(0);
    // Mirrors `open` for the event handlers: a celebration landing while the
    // modal is already up updates the plaque in place — it must not replay
    // the fanfare over itself.
    const openRef = useRef(false);
    openRef.current = open;

    // Every open — manual or celebration — restarts the 2h gate AND the
    // viewing countdown.
    const armOpen = () => {
        noteTrophyOpen();
        const seconds = trophyGateConfig().modalSeconds;
        setExpiresAt(Date.now() + seconds * 1000);
        setSecondsLeft(seconds);
        setOpen(true);
    };
    const armOpenRef = useRef(armOpen);
    armOpenRef.current = armOpen;

    useEffect(() => {
        const onOpen = (e: Event) => {
            const detail = (e as CustomEvent<{ flow?: TrophyFlow }>).detail;
            if (detail?.flow) setFlow(detail.flow);
            setCelebration(null);
            setTab("milestones");
            armOpenRef.current();
        };
        const onCelebrate = (e: Event) => {
            const detail = (e as CustomEvent<TrophyCelebration>).detail;
            if (!detail) return;
            setCelebration(detail);
            if (detail.kind === "record") {
                setFlow(detail.flow);
                setTab("records");
            } else {
                setTab("milestones");
            }
            if (!openRef.current && celebrationSoundEnabled()) playMilestoneFanfare();
            armOpenRef.current();
        };
        window.addEventListener(TROPHY_OPEN_EVENT, onOpen as EventListener);
        window.addEventListener(TROPHY_CELEBRATION_EVENT, onCelebrate as EventListener);
        return () => {
            window.removeEventListener(TROPHY_OPEN_EVENT, onOpen as EventListener);
            window.removeEventListener(TROPHY_CELEBRATION_EVENT, onCelebrate as EventListener);
        };
    }, []);

    useEffect(() => {
        if (!open) return;
        let cancelled = false;
        void fetchTrophies(flow).then(s => {
            if (!cancelled && s) setSnap(s);
        });
        return () => { cancelled = true; };
    }, [open, flow]);

    const close = useCallback(() => {
        setOpen(false);
        setCelebration(null);
        setExpiresAt(null);
        void markTrophiesSeen();
    }, []);

    // Tick the countdown; the modal closes itself when it runs out.
    useEffect(() => {
        if (!open || expiresAt === null) return;
        const tick = () => {
            const left = Math.ceil((expiresAt - Date.now()) / 1000);
            if (left <= 0) {
                close();
                return;
            }
            setSecondsLeft(left);
        };
        tick();
        const timer = setInterval(tick, 250);
        return () => clearInterval(timer);
    }, [open, expiresAt, close]);

    // Any keystroke while open closes the modal. The scanner IS a keyboard,
    // so the first character of the next scan dismisses the popup and the
    // scan itself carries on into the page underneath (the scan listener is
    // window-level and nothing here swallows keys). The short grace period
    // stops the tail of the scan that TRIGGERED a celebration from closing
    // it before it was ever seen.
    useEffect(() => {
        if (!open) return;
        const openedAt = Date.now();
        const onKey = () => {
            if (Date.now() - openedAt < SCAN_DISMISS_GRACE_MS) return;
            close();
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [open, close]);

    if (!open) return null;

    const user = storedUser();
    const locale = language === "pl" ? "pl-PL" : "en-GB";
    const fmtDate = (iso: string) =>
        new Date(iso).toLocaleDateString(locale, { day: "numeric", month: "short" });
    const windowLabel = (w: number) =>
        w < 60 ? `${w} ${t("trophies_min")}` : `${w / 60} ${t("trophies_hr")}`;
    const flowLabel = (f: string) => t(`trophies_flow_${f}`);

    const achievedAt = new Map((snap?.milestones ?? []).map(m => [m.code, m.achievedAt]));
    const goldenReached = GOLDEN_HOUR_LADDER.filter(v => achievedAt.has(`golden_hour_${v}`)).pop() ?? 0;

    const record = (w: number) => snap?.records.find(r => r.windowMinutes === w) ?? null;
    const pb = (w: number) => snap?.personalBests.find(b => b.windowMinutes === w) ?? null;

    const renderChips = (
        prefix: string,
        ladder: number[],
        reached: number,
        opts: { compact?: boolean; format?: (v: number) => string } = {},
    ) => {
        const fmt = opts.format ?? ((v: number) => (v >= 1000 ? `${v / 1000}k` : `${v}`));
        const { achieved, next, hidden } = nextThreeRule(ladder, reached);
        const isFreshlyCelebrated = (v: number) =>
            celebration?.kind === "milestone" && celebration.code === `${prefix}${v}`;
        return (
            <div className={`trophy-chip-grid${opts.compact ? " trophy-chip-grid--4" : ""}`}>
                {achieved.map(v => (
                    <div key={v} className={`trophy-chip${isFreshlyCelebrated(v) ? " trophy-chip--new" : ""}`}>
                        {/* Every ladder wears its own metal: medals for the
                            career, bolts for big days, clocks for the golden
                            hour, flames for streaks. */}
                        {prefix === "streak_" ? (
                            <Flame size={14} color="#E56200" fill="#FDF1E1" />
                        ) : prefix === "day_" ? (
                            <Zap size={14} color="#f59e0b" fill="#f59e0b" />
                        ) : prefix === "golden_hour_" ? (
                            <Clock size={14} color="#eab308" />
                        ) : (
                            <Medal size={14} color="#f59e0b" />
                        )}
                        <span>
                            <span className="trophy-chip-value">{fmt(v)}</span>
                            <span className="trophy-chip-sub">
                                {achievedAt.has(`${prefix}${v}`) ? fmtDate(achievedAt.get(`${prefix}${v}`)!) : <Check size={9} />}
                            </span>
                        </span>
                    </div>
                ))}
                {next.map((v, i) =>
                    i === 0 && reached > 0 ? (
                        <div key={v} className="trophy-chip trophy-chip--progress">
                            <span style={{ flex: 1 }}>
                                <span className="trophy-chip-value" style={{ color: "#E56200" }}>
                                    {fmt(v)}{" "}
                                    <span className="trophy-chip-sub" style={{ display: "inline", color: "#9A5A12" }}>
                                        {Math.min(99, Math.floor((reached / v) * 100))}%
                                    </span>
                                </span>
                                <span className="trophy-chip-bar">
                                    <span
                                        className="trophy-chip-bar-fill"
                                        style={{ width: `${Math.min(99, Math.floor((reached / v) * 100))}%` }}
                                    />
                                </span>
                            </span>
                        </div>
                    ) : (
                        <div key={v} className="trophy-chip trophy-chip--locked">
                            <Lock size={12} />
                            <span className="trophy-chip-value">{fmt(v)}</span>
                        </div>
                    ),
                )}
                {hidden > 0 && (
                    <div className="trophy-chip trophy-chip--ghost">
                        <span className="trophy-chip-sub">+ {hidden} {t("trophies_more")}</span>
                    </div>
                )}
            </div>
        );
    };

    const recordRow = (w: number) => {
        const rec = record(w);
        const isSpotlight =
            celebration?.kind === "record" &&
            celebration.flow === flow &&
            (celebration.windowMinutes === w || (celebration.alsoWindows ?? []).includes(w));
        if (!rec) {
            return (
                <div key={w} className="trophy-row">
                    <span className="trophy-row-window">{windowLabel(w)}</span>
                    <span className="trophy-empty">{t("trophies_no_record")}</span>
                </div>
            );
        }
        return (
            <div
                key={w}
                className={`trophy-row${rec.mine ? " trophy-row--mine" : ""}${isSpotlight ? " trophy-row--spotlight" : ""}`}
            >
                <span className="trophy-row-window">{windowLabel(w)}</span>
                <span className="trophy-row-points">{formatPoints(rec.items)} <span className="trophy-row-items">{t("trophies_items")}</span></span>
                <span className="trophy-row-holder">
                    {rec.mine && <Crown size={11} color="#f59e0b" />}
                    {rec.mine
                        ? t("trophies_you")
                        : `${rec.name ?? `#${rec.uid}`}${rec.warehouse ? ` · ${rec.warehouse}` : ""}`}
                </span>
                <span className="trophy-row-right">
                    {t("trophies_held")} {daysSince(rec.setAt)}{t("trophies_days_short")}
                </span>
            </div>
        );
    };

    const pbRow = (w: number) => {
        const best = pb(w);
        const rec = record(w);
        if (!best) {
            return (
                <div key={w} className="trophy-row">
                    <span className="trophy-row-window">{windowLabel(w)}</span>
                    <span className="trophy-empty">{t("trophies_no_pb")}</span>
                </div>
            );
        }
        const holdsWr = rec !== null && rec.mine && rec.items === best.items;
        return (
            <div key={w} className={`trophy-row${holdsWr ? " trophy-row--mine" : ""}`}>
                <span className="trophy-row-window">{windowLabel(w)}</span>
                <span className="trophy-row-points">{formatPoints(best.items)} <span className="trophy-row-items">{t("trophies_items")}</span></span>
                <span className="trophy-row-holder">{fmtDate(best.setAt)}</span>
                {holdsWr ? (
                    <span className="trophy-wr-chip"><Crown size={10} /> WR</span>
                ) : (
                    <span className="trophy-row-right">
                        {rec ? `${formatPoints(rec.items - best.items)} ${t("trophies_to_wr")}` : ""}
                    </span>
                )}
            </div>
        );
    };

    const plaque = celebration && (
        <div className={`trophy-plaque${celebration.kind === "milestone" ? " trophy-plaque--gold" : ""}`}>
            {celebration.kind === "record" ? (
                <Crown size={40} color="#fbbf24" />
            ) : (
                <Trophy size={40} color="#fbbf24" />
            )}
            <span className="trophy-plaque-label">
                {celebration.kind === "record" ? t("trophies_new_record") : t("trophies_milestone_unlocked")}
            </span>
            <span className="trophy-plaque-value">
                {formatPoints(celebration.kind === "record" ? celebration.items : celebration.value)}
            </span>
            {celebration.kind === "record" ? (
                <>
                    <span className="trophy-plaque-label" style={{ letterSpacing: "0.12em", fontSize: "0.72rem" }}>
                        {t("trophies_items_processed")}
                    </span>
                    <span className="trophy-plaque-chip">
                        {flowLabel(celebration.flow)} · {windowLabel(celebration.windowMinutes)}
                    </span>
                    {(celebration.alsoWindows?.length ?? 0) > 0 && (
                        <span className="trophy-plaque-items">
                            {t("trophies_also_broken")}: {celebration.alsoWindows!.map(w => windowLabel(w)).join(" · ")}
                        </span>
                    )}
                    {celebration.previous && (
                        <span className="trophy-plaque-context">
                            {t("trophies_previous")}: {celebration.previous.name ?? "—"} — {formatPoints(celebration.previous.items)} {t("trophies_items")} · {celebration.previous.heldDays}{t("trophies_days_short")}
                        </span>
                    )}
                </>
            ) : (
                <span className="trophy-plaque-chip">
                    {celebration.code.startsWith("lifetime_")
                        ? t("trophies_lifetime_points")
                        : celebration.code.startsWith("day_")
                            ? t("trophies_in_one_day")
                            : celebration.code.startsWith("golden_hour_")
                                ? t("trophies_in_one_hour")
                                : t("trophies_day_streak")}
                </span>
            )}
        </div>
    );

    return (
        <div className="trophy-modal" data-testid="trophy-modal">
            {celebration && SPARKS.map(([left, top, size, color, delay], i) => (
                <span
                    key={i}
                    className="trophy-spark"
                    style={{
                        left: `${left}%`,
                        top: `${top}%`,
                        width: size,
                        height: size,
                        background: color,
                        boxShadow: `0 0 ${size + 4}px 2px ${color}`,
                        animationDelay: `${delay}s`,
                    }}
                />
            ))}

            <div className="trophy-header">
                <span className="trophy-header-icon"><Trophy size={19} /></span>
                <div>
                    <div className="trophy-header-title">{t("trophies_title")}</div>
                    <div className="trophy-header-sub">
                        {[user.name, user.warehouse].filter(Boolean).join(" · ")}
                    </div>
                </div>
                <span
                    className={`trophy-countdown${secondsLeft <= 10 ? " trophy-countdown--pulse" : ""}`}
                    data-testid="trophy-countdown"
                >
                    {secondsLeft}s
                </span>
                <button className="trophy-close" onClick={close} aria-label={t("trophies_close")}>
                    <X size={30} strokeWidth={2.6} />
                </button>
            </div>

            <div className="trophy-tabs">
                <button
                    className={`trophy-tab${tab === "milestones" ? " trophy-tab--active" : ""}`}
                    onClick={() => setTab("milestones")}
                >
                    {t("trophies_tab_milestones")}
                </button>
                <button
                    className={`trophy-tab${tab === "records" ? " trophy-tab--active" : ""}`}
                    onClick={() => setTab("records")}
                >
                    {t("trophies_tab_records")}
                </button>
            </div>

            {tab === "milestones" && (
                <div className="trophy-stats">
                    <div className="trophy-stat">
                        <span className="trophy-stat-value">{formatPoints(snap?.bestDay ?? 0)}</span>
                        <span className="trophy-stat-label">{t("trophies_best_day")}</span>
                    </div>
                    <div className="trophy-stat">
                        <span className="trophy-stat-value trophy-stat-value--accent">
                            {formatPoints(snap?.lifetime ?? 0)}
                        </span>
                        <span className="trophy-stat-label">{t("trophies_lifetime")}</span>
                    </div>
                    <div className="trophy-stat">
                        <span className="trophy-stat-value">{snap?.streakDays ?? 0}</span>
                        <span className="trophy-stat-label">{t("trophies_streak")}</span>
                    </div>
                </div>
            )}

            <div className="trophy-body">
                {plaque}

                {tab === "milestones" ? (
                    <>
                        <div className="trophy-section-head">
                            <span className="trophy-section-plaque trophy-section-plaque--gold"><Trophy size={14} /></span>
                            <span className="trophy-section-title">{t("trophies_section_lifetime")}</span>
                            <span className="trophy-section-sub">
                                {LIFETIME_LADDER.filter(v => (snap?.lifetime ?? 0) >= v).length} / {LIFETIME_LADDER.length}
                            </span>
                        </div>
                        {renderChips("lifetime_", LIFETIME_LADDER, snap?.lifetime ?? 0, {
                            format: v => (v >= 1_000_000 ? `${v / 1_000_000}M` : `${v / 1000}k`),
                        })}

                        <div className="trophy-section-head">
                            <span className="trophy-section-plaque trophy-section-plaque--orange"><Zap size={14} /></span>
                            <span className="trophy-section-title">{t("trophies_section_day")}</span>
                            <span className="trophy-section-sub">
                                {t("trophies_best_day")} {formatPoints(snap?.bestDay ?? 0)}
                            </span>
                        </div>
                        {renderChips("day_", DAY_LADDER, snap?.bestDay ?? 0, { compact: true })}

                        <div className="trophy-section-head">
                            <span className="trophy-section-plaque trophy-section-plaque--gold"><Clock size={14} /></span>
                            <span className="trophy-section-title">{t("trophies_section_golden_hour")}</span>
                        </div>
                        {renderChips("golden_hour_", GOLDEN_HOUR_LADDER, goldenReached)}

                        <div className="trophy-section-head">
                            <span className="trophy-section-plaque trophy-section-plaque--flame"><Flame size={14} /></span>
                            <span className="trophy-section-title">{t("trophies_section_streak")}</span>
                            <span className="trophy-section-sub">
                                {snap?.streakDays ?? 0} {t("trophies_streak_days")}
                            </span>
                        </div>
                        {renderChips("streak_", STREAK_LADDER, snap?.streakDays ?? 0, {
                            compact: true,
                            format: v => `${v}`,
                        })}
                        <div className="trophy-streak-note">{t("trophies_streak_note")}</div>
                    </>
                ) : (
                    <>
                        <div className="trophy-flows">
                            {TROPHY_DEPARTMENTS.map(dept => (
                                <div key={dept.key} className="trophy-flow-group">
                                    <span className="trophy-flow-dept">{t(`trophies_dept_${dept.key}`)}</span>
                                    <div className="trophy-flow-group-chips">
                                        {dept.flows.map(f => (
                                            <button
                                                key={f}
                                                className={`trophy-flow-chip${f === flow ? " trophy-flow-chip--active" : ""}`}
                                                onClick={() => setFlow(f)}
                                            >
                                                {t(FLOW_SHORT_KEY[f])}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>

                        {(snap?.dethroned ?? []).map((d, i) => (
                            <div key={i} className="trophy-dethroned">
                                <Crown size={14} />
                                <span>
                                    {t("trophies_dethroned")}: {flowLabel(d.flow)} · {windowLabel(d.windowMinutes)} — {d.byName ?? "—"} ({formatPoints(d.points)})
                                </span>
                            </div>
                        ))}

                        <div className="trophy-section-head">
                            <span className="trophy-section-plaque trophy-section-plaque--gold"><Globe size={14} /></span>
                            <span className="trophy-section-title">{t("trophies_worldwide")}</span>
                            <span className="trophy-section-sub">{t("trophies_worldwide_sub")}</span>
                        </div>
                        <div className="trophy-rows">{RECORD_WINDOWS_MIN.map(w => recordRow(w))}</div>

                        <div className="trophy-section-head">
                            <span className="trophy-section-plaque trophy-section-plaque--orange"><Award size={14} /></span>
                            <span className="trophy-section-title">{t("trophies_personal")}</span>
                            <span className="trophy-section-sub">{t("trophies_personal_sub")}</span>
                        </div>
                        <div className="trophy-rows">{RECORD_WINDOWS_MIN.map(w => pbRow(w))}</div>
                    </>
                )}
            </div>
        </div>
    );
};

export default TrophyModal;
